import { useEffect, useState, type FormEvent } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  AlertCircle,
  Bell,
  BellRing,
  CheckCheck,
  CheckCircle2,
  MessageSquareText,
  Smartphone,
} from "lucide-react";
import { AppShell } from "../components/AppShell";
import { Button } from "../components/ui/Button";
import { Input } from "../components/ui/Input";
import { Label } from "../components/ui/Label";
import { Select } from "../components/ui/Select";
import { Card, CardBody } from "../components/ui/Card";
import { Switch } from "../components/ui/Switch";
import { api, ApiException } from "../lib/api";
import { cn } from "../lib/cn";

interface NotifResp {
  phone_e164: string | null;
  phone_carrier: string | null;
  sms_enabled: boolean;
  sms_on_visit_created: boolean;
  sms_on_visit_accepted: boolean;
  sms_on_visit_escalation: boolean;
  carriers: Array<{ slug: string; label: string }>;
}

function digits(s: string): string {
  return s.replace(/\D+/g, "");
}

/** Reads input, returns a US display string like "(314) 555-1234". */
function formatAsTyped(raw: string): string {
  const d = digits(raw).slice(0, 10);
  if (d.length === 0) return "";
  if (d.length <= 3) return `(${d}`;
  if (d.length <= 6) return `(${d.slice(0, 3)}) ${d.slice(3)}`;
  return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
}

function toE164(input: string): string | null {
  const d = digits(input);
  if (d.length === 10) return "+1" + d;
  if (d.length === 11 && d.startsWith("1")) return "+" + d;
  if (input.startsWith("+") && d.length >= 7 && d.length <= 15) return "+" + d;
  return null;
}

function fromE164(e164: string | null): string {
  if (!e164) return "";
  const d = digits(e164);
  const ten = d.length === 11 && d.startsWith("1") ? d.slice(1) : d;
  return formatAsTyped(ten);
}

// Static SMS previews (matched to lib/sms-templates.ts shapes, with sample names).
const PREVIEWS = {
  created: "Carol Care: John posted Dr. Vallala for Carol — arrive Fri Sep 4, 3:15 PM CT. Tap to accept: https://carolcare.io/app/visits/…",
  accepted: "Carol Care: Mark is covering Dr. Vallala for Carol (Fri Sep 4, 3:15 PM CT). https://carolcare.io/app/visits/…",
  escalation: "URGENT — Carol's Dr. Vallala (Fri Sep 4, 3:15 PM CT) still has no acceptor. Tap to take it: https://carolcare.io/app/visits/…",
};

export function Notifications() {
  const navigate = useNavigate();
  const [data, setData] = useState<NotifResp | null>(null);
  const [phoneInput, setPhoneInput] = useState("");
  const [carrier, setCarrier] = useState("");
  const [enabled, setEnabled] = useState(false);
  const [onCreated, setOnCreated] = useState(true);
  const [onAccepted, setOnAccepted] = useState(true);
  const [onEscalation, setOnEscalation] = useState(true);
  const [busy, setBusy] = useState(false);
  const [info, setInfo] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    api
      .get<NotifResp>("/api/me/notifications")
      .then((d) => {
        setData(d);
        setPhoneInput(fromE164(d.phone_e164));
        setCarrier(d.phone_carrier ?? "");
        setEnabled(d.sms_enabled);
        setOnCreated(d.sms_on_visit_created);
        setOnAccepted(d.sms_on_visit_accepted);
        setOnEscalation(d.sms_on_visit_escalation);
      })
      .catch((err) => {
        if (err instanceof ApiException && err.status === 401) navigate("/signin", { replace: true });
      });
  }, [navigate]);

  function markDirty<T>(setter: (v: T) => void) {
    return (v: T) => {
      setter(v);
      setDirty(true);
      setInfo(null);
    };
  }

  async function onDeleteAccount() {
    const typed = prompt(
      "This permanently deletes your Carol Care account and removes you from every circle. Type DELETE to confirm.",
    );
    if (typed !== "DELETE") return;
    try {
      await api.post("/api/me/delete");
      // Cookie is cleared server-side. Hard navigate to the landing page so
      // any cached client state is dropped.
      window.location.href = "/";
    } catch (err) {
      setError(err instanceof ApiException ? err.message : "Couldn't delete the account.");
    }
  }

  async function save(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    setInfo(null);

    let phoneE164: string | null = null;
    if (phoneInput.trim()) {
      phoneE164 = toE164(phoneInput.trim());
      if (!phoneE164) {
        setError("That phone number doesn't look right. Try (314) 251-1700 or +13142511700.");
        setBusy(false);
        return;
      }
    }
    if (enabled && !phoneE164) {
      setError("Add a phone number to enable SMS notifications.");
      setBusy(false);
      return;
    }
    if (enabled && !carrier) {
      setError("Pick your carrier so we know which gateway to use.");
      setBusy(false);
      return;
    }

    try {
      await api.post("/api/me/notifications", {
        phone_e164: phoneE164,
        phone_carrier: carrier || null,
        sms_enabled: enabled,
        sms_on_visit_created: onCreated,
        sms_on_visit_accepted: onAccepted,
        sms_on_visit_escalation: onEscalation,
      });
      setInfo("Preferences saved.");
      setDirty(false);
    } catch (err) {
      setError(err instanceof ApiException ? err.message : "Save failed.");
    } finally {
      setBusy(false);
    }
  }

  if (!data) {
    return (
      <AppShell activeTab="notifications">
        <div className="p-5 text-text-muted">Loading…</div>
      </AppShell>
    );
  }

  return (
    <AppShell activeTab="notifications">
      <form onSubmit={save} className="mx-auto max-w-2xl px-5 py-6 sm:py-8 pb-40 sm:pb-32 space-y-4">
        <div className="flex items-center gap-3">
          <div className="h-11 w-11 rounded-xl bg-primary-soft border border-primary/15 flex items-center justify-center">
            <Bell className="h-5 w-5 text-primary" />
          </div>
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">Notifications</h1>
            <p className="text-sm text-text-muted">Email's on by default. Add a phone for text alerts on what matters.</p>
          </div>
        </div>

        {/* Phone card */}
        <Card>
          <CardBody>
            <SectionHeader icon={<Smartphone className="h-4 w-4" />} label="Phone for SMS" />
            <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label htmlFor="phone">Mobile number</Label>
                <Input
                  id="phone"
                  type="tel"
                  inputMode="tel"
                  autoComplete="tel"
                  placeholder="(314) 555-1234"
                  value={phoneInput}
                  onChange={(e) => markDirty(setPhoneInput)(formatAsTyped(e.target.value))}
                />
              </div>
              <div>
                <Label htmlFor="carrier">Carrier</Label>
                <Select id="carrier" value={carrier} onChange={(e) => markDirty(setCarrier)(e.target.value)}>
                  <option value="">— Pick your carrier —</option>
                  {data.carriers.map((c) => (
                    <option key={c.slug} value={c.slug}>{c.label}</option>
                  ))}
                </Select>
              </div>
            </div>
            <p className="mt-2 text-xs text-text-muted">
              Sent through your carrier's free email-to-SMS gateway. Carrier becomes optional when we move to a real SMS line.
            </p>
          </CardBody>
        </Card>

        {/* Master switch card */}
        <Card>
          <CardBody>
            <div className="flex items-start justify-between gap-4">
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <BellRing className="h-4 w-4 text-primary" />
                  <h2 className="font-semibold">Enable SMS notifications</h2>
                </div>
                <p className="mt-1 text-sm text-text-muted">
                  Master toggle. When off, you only get email — your safe default.
                </p>
              </div>
              <Switch checked={enabled} onChange={markDirty(setEnabled)} ariaLabel="Enable SMS notifications" />
            </div>
          </CardBody>
        </Card>

        {/* Per-event card */}
        <Card className={cn(!enabled && "opacity-50 pointer-events-none")}
              aria-disabled={!enabled}>
          <CardBody>
            <SectionHeader icon={<CheckCheck className="h-4 w-4" />} label="Text me when…" />
            <div className="mt-2 space-y-2">
              <ToggleRow
                title="A new visit is posted"
                desc="A doctor visit was just added and needs coverage."
                checked={onCreated}
                onChange={markDirty(setOnCreated)}
                preview={PREVIEWS.created}
              />
              <ToggleRow
                title="Someone covers a visit"
                desc="The whole circle gets a heads-up on who took it."
                checked={onAccepted}
                onChange={markDirty(setOnAccepted)}
                preview={PREVIEWS.accepted}
              />
              <ToggleRow
                title="A visit is under 24 hours away with no acceptor"
                desc="The urgent escalation. Recommended even if you turn off the others."
                checked={onEscalation}
                onChange={markDirty(setOnEscalation)}
                preview={PREVIEWS.escalation}
                urgent
              />
            </div>
          </CardBody>
        </Card>

        {info && (
          <div className="rounded-md bg-success-soft border border-green-200 text-success text-sm px-3 py-2 flex items-center gap-2">
            <CheckCircle2 className="h-4 w-4" /> {info}
          </div>
        )}
        {error && (
          <div role="alert" className="rounded-md bg-danger-soft border border-red-200 text-danger text-sm px-3 py-2 flex items-center gap-2">
            <AlertCircle className="h-4 w-4" /> {error}
          </div>
        )}

        {/* Danger zone — close account */}
        <Card className="border-danger/30">
          <CardBody>
            <h2 className="text-sm font-semibold text-danger uppercase tracking-wide">Close account</h2>
            <p className="mt-2 text-sm text-text-muted">
              Permanently delete your Carol Care account. You'll be removed from every circle you're in. Visits and notes you created stay in the circle for the family record but your name is removed. This action is irreversible.
            </p>
            <button
              type="button"
              onClick={onDeleteAccount}
              className="mt-4 inline-flex h-11 items-center justify-center px-4 rounded-lg text-[15px] font-semibold border border-danger/40 text-danger hover:bg-danger-soft"
            >
              Delete my account
            </button>
          </CardBody>
        </Card>

        {/* Sticky save bar */}
        <div
          className="fixed bottom-0 sm:left-64 inset-x-0 z-20 bg-surface/95 backdrop-blur border-t border-border"
          style={{ paddingBottom: "env(safe-area-inset-bottom, 0px)" }}
        >
          <div className="mx-auto max-w-2xl px-5 py-3 flex items-center justify-between gap-3">
            <div className="text-sm text-text-muted truncate">
              {dirty ? "Unsaved changes" : enabled ? "SMS is on" : "Email-only"}
            </div>
            <div className="flex gap-2">
              <Link to="/app">
                <Button type="button" variant="ghost">Cancel</Button>
              </Link>
              <Button type="submit" loading={busy} disabled={!dirty && !busy}>
                Save
              </Button>
            </div>
          </div>
        </div>
      </form>
    </AppShell>
  );
}

function SectionHeader({ icon, label }: { icon: React.ReactNode; label: string }) {
  return (
    <div className="flex items-center gap-2 text-sm font-semibold text-text-muted uppercase tracking-wide">
      {icon} {label}
    </div>
  );
}

function ToggleRow({
  title,
  desc,
  checked,
  onChange,
  preview,
  urgent,
}: {
  title: string;
  desc: string;
  checked: boolean;
  onChange: (b: boolean) => void;
  preview: string;
  urgent?: boolean;
}) {
  return (
    <div className="rounded-lg border border-border bg-surface p-3 sm:p-4">
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <div className={cn("text-sm font-medium", urgent && "flex items-center gap-1.5")}>
            {urgent && <BellRing className="h-3.5 w-3.5 text-danger" />}
            {title}
          </div>
          <div className="mt-0.5 text-xs text-text-muted">{desc}</div>
        </div>
        <Switch checked={checked} onChange={onChange} ariaLabel={title} />
      </div>
      {checked && (
        <div className={cn(
          "mt-3 rounded-md px-3 py-2 text-xs leading-relaxed border",
          urgent ? "bg-danger-soft border-red-200 text-danger" : "bg-surface-2 border-border text-text-muted",
        )}>
          <div className="font-semibold text-[10px] uppercase tracking-wider mb-1 opacity-70">
            What they'll see
          </div>
          {preview}
        </div>
      )}
    </div>
  );
}
